import {KeyboardEvent, useEffect, useId, useRef, useState} from 'react';
import Icon from '../Icon';
import {usePlantSuggest} from '../../api/posts';

// The plants a tell is about, usually one: chips for the ones chosen, then free text with
// suggestions from the gardener's own history ("Tomato · Moneymaker") to add another. A custom
// list rather than <datalist>, which iOS renders poorly. Suggestions draw only from this user.
// Typing and leaving the field (or tapping Save, which blurs it first) keeps what was typed.
type Props = {values: string[]; onChange: (values: string[]) => void; hint?: string | null};

const MAX_PLANTS = 10;

export default function PlantField({values, onChange, hint}: Props) {
    const [draft, setDraft] = useState('');
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const listId = useId();
    const wrapper = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handle = window.setTimeout(() => setQ(draft), 150);
        return () => window.clearTimeout(handle);
    }, [draft]);

    const suggestions = usePlantSuggest(q.trim());
    const chosen = new Set(values.map(v => v.trim().toLowerCase()));
    const items = (suggestions.data ?? []).filter(s => !chosen.has(s.label.toLowerCase()));

    useEffect(() => {
        if (!open) return;
        const onDown = (e: PointerEvent) => {
            if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [open]);

    const add = (label: string) => {
        const clean = label.replace(/\s+/g, ' ').trim();
        setDraft('');
        setOpen(false);
        if (clean === '' || chosen.has(clean.toLowerCase()) || values.length >= MAX_PLANTS) return;
        onChange([...values, clean]);
    };
    const remove = (index: number) => onChange(values.filter((_, i) => i !== index));

    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(draft);
        } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
            remove(values.length - 1);
        }
    };

    return (
        <div ref={wrapper} className="relative flex flex-col gap-1">
            <span className="band-label text-ink-2">{values.length > 1 ? 'Plants' : 'Plant'}</span>
            <div className="field flex flex-wrap items-center gap-2">
                <Icon name="leaf" size={20} className="shrink-0 text-shoot-text" />
                {values.map((plant, i) => (
                    <span key={plant} className="chip inline-flex max-w-full items-center gap-1 py-1 pl-3 pr-1">
                        <span className="truncate">{plant}</span>
                        <button type="button" aria-label={`Remove ${plant}`} className="flex h-7 w-7 items-center justify-center rounded-pill text-ink-3" onClick={() => remove(i)}>
                            <Icon name="close" size={14} strokeWidth={2.2} />
                        </button>
                    </span>
                ))}
                {values.length < MAX_PLANTS && (
                    <input
                        type="text"
                        className="min-w-[8ch] flex-1 bg-transparent font-semibold outline-none"
                        placeholder={values.length === 0 ? 'Tomato · Moneymaker' : 'and another…'}
                        autoComplete="off"
                        autoCapitalize="words"
                        maxLength={130}
                        value={draft}
                        onChange={e => {
                            setDraft(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => add(draft)}
                        onKeyDown={onKey}
                        role="combobox"
                        aria-expanded={open && items.length > 0}
                        aria-controls={listId}
                        aria-autocomplete="list"
                    />
                )}
            </div>
            {open && items.length > 0 && (
                <ul id={listId} role="listbox" className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 list-none overflow-y-auto rounded-md border-[1.5px] border-line bg-surface p-1 shadow-float">
                    {items.map(s => (
                        <li key={s.id} role="option" aria-selected={false}>
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left hover:bg-sunken"
                                // pointerdown, not click: the input's blur would otherwise add the typed text first.
                                onPointerDown={e => {
                                    e.preventDefault();
                                    add(s.label);
                                }}
                            >
                                <Icon name="leaf" size={16} className="text-kind-grow-fg" />
                                <span className="flex-1 truncate font-semibold">{s.label}</span>
                                <span className="meta">{s.useCount} {s.useCount === 1 ? 'tell' : 'tells'}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {hint && <span className="meta">{hint}</span>}
        </div>
    );
}
